import { useMemo } from 'react';
import type { FilterType } from '../../../../../../api/screensService/getScreensFilterOptions/enums';
import { useFilterHeader } from '../useFilterHeader';
import { useClickMore } from '../MultiImageFilter/useClickMore';
import { ChipBox } from './ChipBox';
import type { SetFilterListBottomSheetViaQueryValueProps } from '../../Provider/filterListBottomSheet/useFilterListBottomSheetAtom';
import { Flex, Typography } from '@banhala/ui-kit';
import type { SetListHeight } from '../../hooks/useListHeight';
import { RenderItemWrapper } from '../RenderItemWrapper';

interface IMultiChildGroup {
  title: string;
  type: FilterType.MultiImage | FilterType.MultiFlow;
  entity: {
    queryName: string;
    previewItemCount: number;
    possibleItems: {
      text: string;
      queryValue: string;
      countText: string;
      isSelected: boolean;
      image?: string;
    }[];
  };
  handleClickFilter: (
    props: SetFilterListBottomSheetViaQueryValueProps & { title: string },
  ) => void;
  setListHeight: SetListHeight;
  index: number;
}

type MultiFlowProps = IMultiChildGroup;
export const MultiFlowFilter = ({
  index,
  entity: { possibleItems, previewItemCount, queryName },
  title,
  handleClickFilter,
  setListHeight,
}: MultiFlowProps) => {
  const { Header } = useFilterHeader({ title });
  const { isClickedMore, MoreButton } = useClickMore();

  const selectedValues = possibleItems
    .filter(({ isSelected }) => isSelected)
    .map(({ queryValue }) => queryValue);

  const CHIPS = useMemo(() => {
    return possibleItems.map(({ text, isSelected, countText, queryValue }) => {
      return {
        label: text,
        value: queryValue,
        isSelected,
        countText,
      };
    });
  }, [possibleItems]);

  return (
    <RenderItemWrapper
      setListHeight={setListHeight}
      index={index}
      title={title}
    >
      <Header />
      <Flex.Row flexWrap={'wrap'} rowGap={12} columnGap={8}>
        {CHIPS.map(({ value, label, isSelected, countText }, index) => {
          const newValues = isSelected
            ? selectedValues.filter(
                selectedValue => !selectedValue.includes(value),
              )
            : selectedValues.concat([value]);

          if (!isClickedMore && index + 1 > previewItemCount) {
            return null;
          }
          return (
            <ChipBox
              key={`${title}-${value}`}
              isSelected={isSelected}
              onPress={() => {
                handleClickFilter({ queryName, queryValue: newValues, title });
              }}
            >
              <Typography
                type="subtitle3"
                color={
                  isSelected ? 'content_always_white' : 'content_secondary'
                }
              >
                {label}
              </Typography>
              <Typography
                type="subtitle3"
                color={isSelected ? 'content_always_white' : 'content_tertiary'}
                marginLeft={2}
              >
                {countText}
              </Typography>
            </ChipBox>
          );
        })}
      </Flex.Row>
      {possibleItems.length > previewItemCount && MoreButton}
    </RenderItemWrapper>
  );
};
